// pages/EntreprisesPage.tsx
import { useState, useEffect } from "react";
import { Search, Plus, Eye, Edit, Trash2, Ban, Phone } from "lucide-react";
import EntrepriseList from "../../../components/features/entreprises/EntreprisesList";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { Entreprise } from "../../../types/features/Entreprise";
import PageListLayout from "../../../layouts/PageListLayout";
import { useNavigate } from "react-router-dom";
import useEntreprises from "../../../hooks/features/entreprises/useEntreprises";

export default function EntreprisesPage() {
  const { 
    entreprises, 
    loading, 
    pagination,
    getEntreprises,
    deleteEntreprise,
    updateEntreprise
  } = useEntreprises();
  
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Entreprise[]>([]);

  // États de sélection
  const [selectedEntreprise, setSelectedEntreprise] = useState<Entreprise | null>(null);

  // Chargement initial des entreprises
  useEffect(() => {
    getEntreprises(1, 20);
  }, [getEntreprises]);

  const displayEntreprises = searchQuery.trim() ? searchResults : entreprises;

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      const filtered = entreprises.filter(entreprise => 
        entreprise.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entreprise.support_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entreprise.support_phone?.includes(searchQuery) ||
        entreprise.website?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entreprise.pays?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  };

  const handlePhoneSearch = async () => {
    if (searchQuery.trim()) {
      const found = entreprises.find(entreprise => 
        entreprise.support_phone?.includes(searchQuery) ||
        entreprise.twilio_phone_numbers?.some(phone => phone.includes(searchQuery))
      );
      
      if (found) {
        navigate("/support/entreprises/details", { state: found });
      } else {
        alertError("Aucune entreprise trouvée avec ce numéro");
      }
      setSearchQuery("");
      setSearchResults([]);
    }
  };

  const handleDeleteEntreprise = async (id: string) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette entreprise ?")) {
      try {
        const success = await deleteEntreprise(id);
        if (success) {
          alertSuccess("Entreprise supprimée avec succès !");
          await getEntreprises(pagination.currentPage, pagination.perPage);
        }
      } catch (error) {
        alertError("Erreur lors de la suppression de l'entreprise");
        console.error(error);
      }
    }
  };

  const handleSuspendreEntreprise = async (entreprise: Entreprise) => {
    const newStatus = entreprise.subscription_status === "suspended" ? "active" : "suspended";
    const action = newStatus === "suspended" ? "suspendre" : "réactiver";
    
    if (window.confirm(`Êtes-vous sûr de vouloir ${action} cette entreprise ?`)) {
      try {
        const result = await updateEntreprise(entreprise.id, { 
          subscription_status: newStatus 
        });
        if (result) {
          alertSuccess(`Entreprise ${action}ée avec succès !`);
          await getEntreprises(pagination.currentPage, pagination.perPage);
        }
      } catch (error) {
        alertError(`Erreur lors de la ${action} de l'entreprise`);
        console.error(error);
      }
    }
  };

  const getActionsMenu = (entreprise: Entreprise): Menu[] => {
    const isSuspended = entreprise.subscription_status === "suspended";
    
    return [
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => {
          navigate("/support/entreprises/edit", { state: entreprise });
          setSelectedEntreprise(null);
        },
      },
      {
        label: isSuspended ? "Réactiver" : "Suspendre",
        icon: Ban,
        onClick: () => {
          handleSuspendreEntreprise(entreprise);
          setSelectedEntreprise(null);
        },
      },
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          navigate("/support/entreprises/details", { state: entreprise });
          setSelectedEntreprise(null);
        },
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          handleDeleteEntreprise(entreprise.id);
          setSelectedEntreprise(null);
        },
      },
    ];
  };

  // Affichage du chargement initial
  if (loading && entreprises.length === 0) {
    return (
      <PageListLayout title="">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#031B4E] border-t-transparent"></div>
        </div>
      </PageListLayout>
    );
  }

  return (
    <PageListLayout title="">
      <div className="px-6 py-5 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-[#031B4E] tracking-tight">Entreprises</h1>
              <p className="text-sm text-slate-400 font-light mt-0.5">
                Recherchez et consultez les informations de votre répertoire d'entreprises.
              </p>
              {pagination.total > 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  {pagination.total} entreprise(s) au total
                </p>
              )}
            </div>
            
            <button
              onClick={() => navigate("/support/entreprises/new")}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Plus size={18} />
              <span>Nouvelle entreprise</span>
            </button>
          </div>

          {/* Zone de Recherche */}
          <div className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email ou téléphone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                />
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleSearch}
                  className="flex-1 sm:flex-none px-5 py-2.5 bg-primary text-white font-medium text-sm rounded-xl hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all shadow-sm"
                >
                  Rechercher
                </button>
                <button
                  onClick={handlePhoneSearch}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-5 py-2.5 bg-[#031B4E] text-white font-medium text-sm rounded-xl hover:bg-[#052975] focus:outline-none focus:ring-4 focus:ring-blue-600/10 transition-all shadow-sm"
                >
                  <Phone size={14} />
                  <span>Par téléphone</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des entreprises */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-[#031B4E] border-t-transparent"></div>
          </div>
        ) : (
          <EntrepriseList
            entreprises={displayEntreprises}
            onAction={(entreprise) => {
              setSelectedEntreprise(entreprise);
            }}
            selectable={false}
          />
        )}
      </div>

      {/* Menu modal volant */}
      {selectedEntreprise && (
        <MenuModal
          title={selectedEntreprise.name || "Actions entreprise"}
          isOpen={true}
          onClose={() => setSelectedEntreprise(null)}
          icon={null}
          menu={getActionsMenu(selectedEntreprise)}
        />
      )}
    </PageListLayout>
  );
}